import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";

import {
  whtsappBg,
  whtsappIcon,
  doubleTick,
} from "../../asset";
import { fetchWhatsappSendList } from "../../features/template/TemplateService";
import { FiCornerDownLeft, FiLink, FiPhone, FiFile } from "react-icons/fi";

interface TemplateListProps {
  activeTab: string;
  selectedTypes: string[];

  onLoadingChange?: (loading: boolean) => void;
  onPageInfoChange?: (pageInfo: any) => void;

  page: number;
  pageSize: number;
  cursor?: string;
}

/* ---------- Helpers ---------- */

const getHeaderData = (components: any[]) => {
  const header = components?.find((c: any) => c.type === "HEADER");

  return {
    format: header?.format || "",
    text: header?.text || "",
    url: header?.example?.header_handle?.[0] || "",
  };
};

export default function TemplateList({
  activeTab,
  selectedTypes,
  onLoadingChange,
  page,
  pageSize,
  cursor,
  onPageInfoChange,
}: TemplateListProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex text-sm gap-1">
      <span className="text-text-secondary font-semibold shrink-0">{label}:</span>
      <span className="text-black font-bold break-all">{value || "-"}</span>
    </div>
  );

  const getComponentText = (components: any[], type: string) =>
    components?.find((c: any) => c.type === type)?.text || "";

  const getHeaderFormat = (components: any[]): string => {
    const header = components?.find((c: any) => c.type === "HEADER");
    if (!header || !header.format) return "Text";
    return header.format.charAt(0) + header.format.slice(1).toLowerCase();
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      onLoadingChange?.(true);
      const result = await dispatch(
        fetchWhatsappSendList({
          size: pageSize,
          after: cursor,
        })
      ).unwrap();

      setTemplates(result.results || []);
      onPageInfoChange?.(result.page); // push pageInfo up to parent
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  // Re-fetch whenever page or pageSize changes
  useEffect(() => { loadTemplates(); }, [page, pageSize, cursor]);

  const filteredTemplates = templates.filter((template) => {
    if (activeTab !== "All") {
      if (template.status?.toUpperCase() !== activeTab.toUpperCase()) return false;
    }
    if (!selectedTypes.includes("All")) {
      const headerFormat = getHeaderFormat(template.components);
      if (!selectedTypes.includes(headerFormat)) return false;
    }
    return true;
  });

  if (filteredTemplates.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-16 text-text-secondary font-semibold">
        No templates found for the selected filters.
      </div>
    );
  }

  return (
    <>
      {filteredTemplates.map((template: any) => {
        const headerData = getHeaderData(template.components);

        const footerComp = template.components?.find((c: any) => c.type === "FOOTER");
        const buttonsComp = template.components?.find((c: any) => c.type === "BUTTONS");

        const bodyText = getComponentText(template.components, "BODY");
        const footerText = footerComp?.text || "";
        const headerFormat = getHeaderFormat(template.components);

        const allButtons = buttonsComp?.buttons || [];
        const ctaButtons = allButtons.filter((b: any) => b.type === "URL" || b.type === "PHONE_NUMBER");
        const replyButtons = allButtons.filter((b: any) => b.type === "QUICK_REPLY");

        return (
          <div
            key={template.id}
            className="flex flex-row border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-md w-full sm:max-w-[280px] overflow-hidden"
          >
            {/* Left section */}
            <div className="flex flex-col gap-4 p-4 flex-1 min-w-0">

              {/* WhatsApp Preview */}
              <div className="rounded-2xl overflow-hidden border border-gray-200">

                {/* Top bar */}
                <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-100">
                  <img src={whtsappIcon} className="w-4 h-4" alt="WhatsApp" />
                  <span className="text-xs font-semibold text-black">
                    Whatsapp Business
                  </span>
                </div>

                {/* Chat background */}
                <div
                  className="p-3 bg-cover bg-center"
                  style={{ backgroundImage: `url(${whtsappBg})` }}
                >
                  <div className="bg-[#E4FEE0] rounded-xl overflow-hidden shadow-sm w-full">

                    {/* Header */}
                    {headerData.format === "TEXT" && (
                      <div className="px-3 pt-2 text-xs font-bold text-black">
                        {headerData.text}
                      </div>
                    )}

                    {headerData.format === "IMAGE" && (
                      <>
                        {headerData.url ? (
                          <img
                            src={headerData.url}
                            alt="Template header"
                            className="w-full h-32 object-cover"
                          />
                        ) : (
                          <div className="w-full h-20 bg-gray-200 flex items-center justify-center text-xs text-gray-400">
                            📷 Image
                          </div>
                        )}
                      </>
                    )}

                    {headerData.format === "VIDEO" && (
                      <>
                        {headerData.url ? (
                          <video controls className="w-full h-32 object-cover">
                            <source src={headerData.url} />
                          </video>
                        ) : (
                          <div className="w-full h-20 bg-gray-200 flex items-center justify-center text-xs text-gray-400">
                            🎥 Video
                          </div>
                        )}
                      </>
                    )}

                    {headerData.format === "DOCUMENT" && (
                      <div className="flex items-center gap-2 px-3 pt-2 text-xs text-blue-600">
                        <FiFile />
                        {headerData.url ? (
                          <a
                            href={headerData.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            View Document
                          </a>
                        ) : (
                          "Document"
                        )}
                      </div>
                    )}

                    {/* Body */}
                    <div className="px-3 py-2 text-xs text-black whitespace-pre-wrap break-words">
                      {bodyText || "No body text"}
                    </div>

                    {/* Footer */}
                    {footerText && (
                      <div className="px-3 pb-1 text-[10px] text-gray-400 italic">
                        {footerText}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="flex items-center justify-end gap-1 px-3 pb-2 text-[10px] text-gray-400">
                      <span>20:13</span>
                      <img src={doubleTick} className="w-3 h-3" alt="Delivered" />
                    </div>

                    {/* CTA Buttons */}
                    {ctaButtons.map((btn: any, i: number) => (
                      <div
                        key={i}
                        className="flex items-center justify-center gap-1 py-1.5 text-[#00A5F4] text-[10px] font-semibold border-b border-green-100 last:border-b-0"
                      >
                        {btn.type === "URL" && <FiLink size={14} />}
                        {btn.type === "PHONE_NUMBER" && <FiPhone size={14} />}
                        <span>{btn.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Quick reply buttons */}
                  {replyButtons.length > 0 && (
                    <div className="flex flex-col gap-1 mt-2">
                      {replyButtons.map((btn: any, i: number) => (
                        <div
                          key={i}
                          className="bg-white text-[#00A5F4] text-[10px] font-semibold py-1.5 px-3 rounded-lg shadow-sm border border-gray-200 text-center w-full flex items-center justify-center gap-1"
                        >
                          <FiCornerDownLeft size={14} />
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Info */}
              <div className="flex flex-col gap-1.5">
                <span className="text-base font-bold text-brand-main truncate">
                  {template.name}
                </span>

                <InfoRow label="Category" value={template.category} />
                <InfoRow label="Language" value={template.language} />
                <InfoRow label="Status" value={template.status} />
                <InfoRow label="Type" value={headerFormat} />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}